// 開発案件モジュールの使い方ガイド — 本文データ（日本語 / English）。
// 文面は与えられた原文のまま。表示側（DevGuide.js）はこの構造を描画するだけで、
// 文章の言い換えや要約はしない。

export const GUIDE_LANG_KEY = 'sensbey.devGuide.lang';

export const UI = {
  ja: {
    eyebrow: '開発案件',
    title: '開発案件の使い方',
    lead: 'はじめて使う方向けの操作ガイドです。',
    toc: '目次',
    lifecycle: '5つの段階',
    langLabel: '表示言語',
    fields: '入力項目',
    back: '開発案件に戻る',
    faq: 'よくある質問',
    faqHint: '質問をクリックすると答えが開きます。',
    updated: 'この内容は開発案件モジュールの現在の動作に基づいています。',
    feedbackComment: 'このガイドにコメントする（ナレッジ）',
    feedbackMail: 'このガイドについて連絡する',
  },
  en: {
    eyebrow: 'Development Project',
    title: 'How to use Development Project',
    lead: 'A walkthrough for someone using this module for the first time.',
    toc: 'Contents',
    lifecycle: 'The five stages',
    langLabel: 'Language',
    fields: 'Fields',
    back: 'Back to Development Projects',
    faq: 'FAQ',
    faqHint: 'Click a question to reveal the answer.',
    updated: 'This reflects the current behaviour of the Development Project module.',
    feedbackComment: 'Comment on this guide (Knowledge)',
    feedbackMail: 'Contact us about this guide',
  },
};

// 5段階。`section` はクリック時のジャンプ先。
export const STAGES = [
  { no: '01', ja: '構想', en: 'Concept', section: 'concept',
    descJa: '何を、なぜ作るのかを整理する段階',
    descEn: "work out what you're building and why" },
  { no: '02', ja: '部品計画', en: 'Parts Planning', section: 'parts',
    descJa: '必要な部品を洗い出し、台数を確定する段階',
    descEn: 'identify the parts you need, confirm quantities' },
  { no: '03', ja: '調達', en: 'Procurement', section: 'parts',
    descJa: '部品の手配を進める段階',
    descEn: 'arrange for the parts' },
  { no: '04', ja: '試作', en: 'Prototype', section: 'prototype',
    descJa: '実際に組み立てて検証する段階',
    descEn: 'build and verify' },
  { no: '05', ja: '完了', en: 'Completion', section: 'completion',
    descJa: '検証が終わり、開発を終了する段階',
    descEn: 'verification is done, development is finished' },
];

export const SECTIONS = [
  {
    id: 'overview',
    navJa: '開発案件とは', navEn: 'What Development Project is',
    titleJa: '開発案件とは', titleEn: 'What Development Project Is',
    blocks: [
      { t: 'p',
        ja: '開発案件は、新しい部品や治具などの開発を「構想」から「完了」まで、ひとつの記録として管理する画面です。ここで検討・試作した部品は、必要になった時点で技術資産(社内の正式な資産台帳)に登録され、以後の調達・保管・棚卸しに使われます。',
        en: "Development Project tracks the development of a new part or fixture as a single record, from initial concept through completion. Parts explored here can, when appropriate, be registered as a Technical Asset (the company's official asset ledger) for procurement and storage going forward." },
      { t: 'p',
        ja: '覚えておくと迷わない役割分担がひとつあります。開発案件は「何を、なぜ作ろうとしているか」という企画・検討の記録です。技術資産は「実在し、追跡できるモノ」の正式な台帳です。',
        en: "One distinction is worth remembering: a Development Project is a planning and review record — what you're building and why. A Technical Asset is the official ledger of something real and trackable." },
      { t: 'callout', tone: 'info',
        ja: '開発案件の中で「未登録部品」として書いた行は、そのままでは技術資産にはなりません。人が内容を確認し、明示的に登録操作をしたときだけ、技術資産になります。',
        en: 'A line entered as an "Unregistered Part" inside a project does not become a Technical Asset by itself — it only becomes one when a person reviews it and explicitly registers it.' },
    ],
  },
  {
    id: 'create',
    navJa: '1. 案件を作成する', navEn: '1. Create a project',
    titleJa: '案件を作成する', titleEn: 'Create a Project',
    blocks: [
      { t: 'p',
        ja: '開発案件一覧の「新規作成」から始めます。最初に入力するのは案件名と、構想提案の基本項目、そして担当者です。',
        en: 'Start from "New" on the Development Project list. The first things you enter are the project title, the basic concept fields (purpose, spec/type), and the owner.' },
      { t: 'fields',
        ja: ['案件名', '目的', '型式・仕様', '担当者'],
        en: ['Title', 'Purpose', 'Spec-Type', 'Owner'] },
      { t: 'callout', tone: 'warn',
        ja: '担当者を未設定のまま進めると、後で試作を開始できません。作成の時点で決まっていなくても構いませんが、部品計画に進む前には必ず設定してください。',
        en: "Before you start — if you leave the owner unset, you won't be able to start the prototype stage later. It's fine to leave it unset while you're still figuring things out, but set it before moving into parts planning." },
    ],
  },
  {
    id: 'concept',
    navJa: '2. 構想提案を編集する', navEn: '2. Edit the concept',
    titleJa: '構想提案を編集する', titleEn: 'Edit the Concept',
    blocks: [
      { t: 'p',
        ja: '構想提案は、案件がどの段階に進んでも編集できます。構想の段階に限らず、調達・試作・完了に進んだ後でも変更可能です。前提や仕様が後から変わったときに、記録を現状に合わせて直すことができます。',
        en: 'The concept can be edited no matter what stage the project is in — not just during Concept, but after it has moved into Procurement, Prototype, or even Completion. If assumptions or requirements change later, you can update the record to match reality.' },
      { t: 'sub', ja: '編集する2つの方法', en: 'Two ways to edit' },
      { t: 'list',
        ja: ['手動で編集する — 案件詳細ページの構想提案欄を直接書き換えて保存します。',
             'AIに整理してもらう — 「AI Workspace」に下書きや箇条書きを渡すと整理案を提案してくれます。内容を確認し「この内容に更新」を押すと反映されます。提案をそのまま自動で確定することはありません。'],
        en: ['Edit manually — edit the concept fields directly on the project detail page and save.',
             'Let AI help organize it — give AI Workspace your rough notes or bullet points and it proposes a cleaned-up version. Review it, then click "Update with this" to apply it. Nothing is applied automatically without your review.'] },
      { t: 'callout', tone: 'info',
        ja: 'どちらの方法で更新しても、詳細ページの「変更履歴」に、誰が・いつ・どの項目を・どう変えたかが追記されます。過去の内容が上書きで消えることはありません。',
        en: 'However you update it, the change is appended to Revision History on the detail page — who changed what, when. Nothing is overwritten; the record only grows.' },
    ],
  },
  {
    id: 'parts',
    navJa: '3. 必要部品を追加する', navEn: '3. Add required parts',
    titleJa: '必要部品を追加する', titleEn: 'Add Required Parts',
    blocks: [
      { t: 'p',
        ja: '「部品計画」タブから「必要部品を追加」を開きます。',
        en: 'Open "Add Required Part" from the Parts Planning tab.' },
      { t: 'p',
        ja: 'マスターから探す: 品名・品番・図番・メーカー品番などで検索すると、技術資産と一般品目の両方から候補が表示されます。既存の資産を見落として同じ部品を二重に扱ってしまうことを防ぐためです。',
        en: "Search the master: searching by name, part number, drawing number, or manufacturer part number returns matches from both Technical Assets and general items, so you don't miss an asset that already exists." },
      { t: 'p',
        ja: '見つからない場合: 未登録部品として入力します。',
        en: "If you can't find it: enter it as an Unregistered Part." },
      { t: 'fields',
        ja: ['品名', '型式・仕様', '図番', '図面リビジョン', 'メーカー', 'メーカー品番',
             '数量(1台あたり)', '品番グループ(任意)', '備考'],
        en: ['Name', 'Spec-Type', 'Drawing No.', 'Drawing Revision', 'Manufacturer',
             'Manufacturer Part No.', 'Qty per unit', 'SKU Group (optional)', 'Notes'] },
      { t: 'p',
        ja: '補足: 品番グループは必須ではありませんが、分かる範囲で選んでおくと、後で技術資産として登録する際に候補として引き継がれます。もちろん、その場で変更・修正することもできます。',
        en: "Note: SKU Group isn't required, but selecting it if you already know it carries forward as a suggestion when this part is later registered as a Technical Asset — and you can still change it at that point." },
      { t: 'callout', tone: 'info',
        ja: '品名を入力している最中に、表記ゆれ(全角・半角・スペースの違いなど)を考慮したうえで似た資産が見つかると案内が出ることがあります。クリックしない限り、自動的に選択されたり統合されたりすることはありません。',
        en: 'While typing a name, you may see a hint that a similar asset already exists (matching is normalized for full-width/half-width and spacing differences). Nothing is auto-selected or merged unless you click it.' },
    ],
  },
  {
    id: 'prototype',
    navJa: '4. 試作を開始する', navEn: '4. Starting the prototype',
    titleJa: '試作を開始する', titleEn: 'Starting the Prototype',
    blocks: [
      { t: 'p',
        ja: '試作を開始できるかどうかは、次の3つの条件だけで判定されます。これ以外の項目は、参考情報として画面に表示されますが、開始そのものを止めることはありません。',
        en: 'Whether the prototype stage can start is decided by exactly three conditions. Everything else is shown as informational context but does not block starting.' },
      { t: 'table',
        headJa: ['条件', '試作への影響', '次にすること'],
        headEn: ['Condition', 'Effect on start', 'Next step'],
        rowsJa: [
          ['部品未計画', 'blocks:開始できない', '「必要部品を追加」で部品を計画する'],
          ['製作台数が未入力', 'blocks:開始できない', '製作予定の台数を入力する'],
          ['担当者が未設定', 'blocks:開始できない', '担当者を設定する'],
          ['構想提案が未入力', 'info:参考情報のみ', '時間があるときに記入する'],
          ['検証が不合格', 'info:参考情報のみ', '検証結果を確認・対応する'],
        ],
        rowsEn: [
          ['Parts not planned', 'blocks:Blocks', 'Add the required parts'],
          ['Build quantity not entered', 'blocks:Blocks', 'Enter the planned quantity'],
          ['Owner not set', 'blocks:Blocks', 'Set an owner'],
          ['Concept not filled in', 'info:Informational only', 'Fill it in when you have time'],
          ['Verification failed', 'info:Informational only', 'Review and address the result'],
        ] },
      { t: 'p',
        ja: '詳細ページの「止まっている理由と次のアクション」カードに、現時点で何が足りないか、次に何をすべきかがまとめて表示されます。',
        en: 'The "Why it\'s stuck / what to do next" card on the detail page lists exactly what\'s missing and what to do about it.' },
    ],
  },
  {
    id: 'promote',
    navJa: '5. 技術資産として登録する', navEn: '5. Promote to Technical Asset',
    titleJa: '技術資産として登録する', titleEn: 'Register as a Technical Asset',
    blocks: [
      { t: 'p',
        ja: '部品計画で「未登録部品」として入力した行のうち、社内で設計・製作した部品などは、正式な技術資産に昇格させる経路があります。部品行にある「技術資産として登録」から進みます。資産を登録フォームが開き、品名・型式・仕様・図番・メーカー・メーカー品番・備考はそのまま引き継がれます。残りの必須項目を入力して保存すると、新しい技術資産が発行されます。',
        en: 'A line entered as an Unregistered Part — typically something designed or made in-house — can be promoted into a full Technical Asset. Use "Register as Technical Asset" on that part line. The Register Asset form opens pre-filled with name, spec/type, drawing no., manufacturer, manufacturer part no., and notes carried over. Fill in the remaining required fields and save to issue the new asset.' },
      { t: 'fields',
        ja: ['品番グループ→品番発番', '現在地', '受注分類'],
        en: ['SKU Group → SKU issuance', 'Location', 'Order Category'] },
      { t: 'callout', tone: 'info',
        ja: '登録された資産には、元になった開発案件へのリンクが残ります。後からその部品がどの案件の要望から生まれたのかを追跡できます。同じ部品行を二重に登録することはできません(一度登録すると、行の表示がリンクに切り替わります)。',
        en: "The new asset keeps a link back to the project it came from, so you can always trace where a part originated. A part line can't be promoted twice — once used, it switches to a link instead of the promote action." },
      { t: 'callout', tone: 'warn',
        ja: '未登録部品が自動的に技術資産になることはありません。必ず人が内容を確認し、必須項目を埋めて保存操作をした場合にのみ、資産として発行されます。',
        en: 'Nothing is registered automatically. An asset is only created when a person reviews the details and explicitly saves.' },
    ],
  },
  {
    id: 'completion',
    navJa: '6. 完了する', navEn: '6. Completion',
    titleJa: '完了する', titleEn: 'Completion',
    blocks: [
      { t: 'p',
        ja: '試作の検証が終わったら、案件を完了に進めます。完了報告書が発行され、対象部署を含む開発の経緯と結果がまとめられます。',
        en: "Once verification is finished, move the project to Completion. A completion report is generated, summarizing the development's history and result, including the target department." },
    ],
  },
  {
    id: 'manage',
    navJa: '7. 案件を管理する', navEn: '7. Manage the project',
    titleJa: '案件を管理する', titleEn: 'Manage the Project',
    blocks: [
      { t: 'sub', ja: '案件名の変更', en: 'Renaming' },
      { t: 'p',
        ja: '詳細ページ上部の案件名をクリックすると、その場で編集できます。保存すると一覧の表示にもすぐに反映されます。',
        en: 'Click the project title at the top of the detail page to edit it in place. Saving updates the list view immediately too.' },
      { t: 'sub', ja: '削除', en: 'Delete' },
      { t: 'p',
        ja: '削除できるのは、案件の担当者本人、または管理者のみです。削除すると、一覧・ダッシュボード・検索など通常の画面からは表示されなくなります。ただし、部品行や、すでに登録済みの技術資産の記録が消えることはありません。',
        en: 'Only the project\'s owner or an admin can delete it. Deleting hides it from the list, dashboard, and search, but part lines and any already-registered Technical Assets are not removed.' },
      { t: 'callout', tone: 'warn',
        ja: '削除する際は、確認のために「削除」という文字を入力する必要があります。入力するまで削除ボタンは押せません。',
        en: 'To prevent accidental deletion, you must type the word "削除" (delete) to confirm before the delete button becomes active.' },
      { t: 'sub', ja: '復元', en: 'Restore' },
      { t: 'p',
        ja: '削除した案件は完全に消去されるわけではありません。管理者は一覧画面の「削除済みを表示」を切り替えることで確認でき、そこから「復元」を選ぶと元の状態に戻ります。',
        en: 'A deleted project isn\'t gone for good. An admin can toggle "Show Deleted" on the list to see it, and Restore returns it to its normal state.' },
      { t: 'callout', tone: 'info',
        ja: 'すでに部品を技術資産として登録済みの案件を削除しても、その資産自体は問題なく使い続けられます。資産側には「削除済み案件」という表示が出て、由来となった開発案件が削除されていることが分かるようになっています。',
        en: 'If a project with an already-registered Technical Asset is deleted, that asset keeps working normally — it just shows a "Deleted Project" badge indicating its origin project has been deleted.' },
    ],
  },
];

export const FAQ = [
  {
    id: 'faq-prototype',
    qJa: '試作を開始できません。なぜですか？',
    qEn: "Why can't I start the prototype stage?",
    aJa: '部品未計画・製作台数未入力・担当者未設定の3つのうち、いずれかが残っています。詳細ページの「止まっている理由と次のアクション」で、具体的に何が足りないかを確認してください。構想提案が未入力であることや検証不合格は、試作の開始そのものは止めません。',
    aEn: 'One of three things is still missing: parts not planned, build quantity not entered, or owner not set. Check the "Why it\'s stuck / what to do next" card for specifics. An unfilled concept or a failed verification does not block starting the prototype.',
  },
  {
    id: 'faq-concept-edit',
    qJa: '構想提案は完了した後でも直せますか？',
    qEn: 'Can I still edit the concept after completion?',
    aJa: '直せます。構想提案はどの段階でも編集できる設計になっています。編集内容はすべて変更履歴に残るため、後から見ても何がいつ変わったかが分かります。',
    aEn: 'Yes. The concept can be edited at any stage, and every edit is recorded in Revision History so you can see what changed and when.',
  },
  {
    id: 'faq-part-vs-asset',
    qJa: '必要部品と技術資産は何が違うのですか？',
    qEn: "What's the difference between a Required Part and a Technical Asset?",
    aJa: '必要部品は「この案件でこれが何個必要か」という、案件に紐づいた計画上の記録です。技術資産は、実在して追跡・保管される正式な資産です。必要部品の行がそのまま技術資産に自動反映されることはなく、人が確認して明示的に登録したときだけ資産になります。',
    aEn: 'A Required Part is a planning-level record scoped to one project — "this project needs N of this." A Technical Asset is the official, trackable record of something real. A Required Part line never becomes a Technical Asset automatically; it only does when a person reviews and explicitly registers it.',
  },
  {
    id: 'faq-deleted',
    qJa: '削除した案件はどこにありますか？なくなってしまいましたか？',
    qEn: 'Where did my deleted project go?',
    aJa: 'なくなってはいません。通常の画面からは見えなくなりますが、記録自体は残っています。管理者であれば、一覧画面の「削除済みを表示」からいつでも確認・復元できます。',
    aEn: "It isn't gone. It's hidden from normal views, but an admin can see and restore it anytime via \"Show Deleted\" on the list.",
  },
];
